import React, { useState, useRef, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ChevronDown, Download, FileText, Camera, Info } from 'lucide-react';
import { getRequest } from '../services/apiService';
import PropTypes from 'prop-types';

const generateStatusColor = (status) => {
  const hash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };

  const h = Math.abs(hash(status)) % 360;
  return `hsl(${h}, 70%, 60%)`;
};

const getStatusDisplayInfo = (status) => {
  const statusMap = {
    'PR TO BE RAISED': { name: 'PR To Be Raised', shortName: 'PR' },
    'TENDERING IP': { name: 'Tendering IP', shortName: 'TI' },
    'TSP not Approved': { name: 'TSP Not Approved', shortName: 'TSP NA' },
    'TSP Approved': { name: 'TSP Approved', shortName: 'TSP A' },
    'ORDERED': { name: 'Ordered', shortName: 'O' },
    'Not Ordered': { name: 'Not Ordered', shortName: 'NO' },
    'Delivered': { name: 'Delivered', shortName: 'Deli.' },
    'Not Delivered': { name: 'Not Delivered', shortName: 'ND' },
    'Partially Delivered': { name: 'Partially Delivered', shortName: 'Parti' },
    'MOD': { name: 'MOD', shortName: 'MOD' },
    'BD Approved': { name: 'BD Approved', shortName: 'BD' }
  };

  return statusMap[status] || {
    name: status,
    shortName: status.split(' ').map(w => w[0]).join('')
  };
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-800 text-base">{payload[0].payload.name}</p>
      </div>
    );
  }
  return null;
};

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  shortName,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {`${shortName}\n${value}`}
    </text>
  );
};

const StatusDetailsModal = ({ data, details, department, project, ship, onClose }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showActivityFlow, setShowActivityFlow] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [showHelpMessage, setShowHelpMessage] = useState(true);
  const [equipmentId, setEquipmentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
    setShowHelpMessage(false);
  };
  // Pagination calculations
  const currentItems = selectedStatus && details[selectedStatus.statusKey] 
    ? details[selectedStatus.statusKey].slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      )
    : [];
    const totalPages = selectedStatus && details[selectedStatus.statusKey]
    ? Math.ceil(details[selectedStatus.statusKey].length / itemsPerPage)
    : 0;
      const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };
  
  const handleViewFlowchart = async (equipmentName, taskName) => {
    try {
      const equipmentResponse = await getRequest('master/equipment/');
      if (equipmentResponse.status === 'success') {
        const foundEquipment = equipmentResponse.data.find(
          eq => eq.name === equipmentName || eq.name.includes(equipmentName)
        );
        
        if (foundEquipment) {
          setEquipmentId(foundEquipment.id);
          setSelectedEquipment({ name: equipmentName, taskName });
          setShowActivityFlow(true);
        } else {
          console.error('Equipment not found:', equipmentName);
          alert('Equipment details not found');
        }
      }
    } catch (err) {
      console.error('Error fetching equipment data:', err);
      alert('Failed to fetch equipment data');
    }
  };

  const contextTitle = () => {
    let title = `Equipment Details - ${department}`;
    if (project) title += ` - ${project.name}`;
    if (ship) title += ` - ${ship.name}`;
    return title;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold">{contextTitle()}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
        </div>
        
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-6">
            {data.map((status) => (
              <button
                key={status.statusKey}
                onClick={() => handleStatusClick(status)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center transition-all ${
                  selectedStatus?.statusKey === status.statusKey 
                    ? 'text-white border-2' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ 
                  backgroundColor: selectedStatus?.statusKey === status.statusKey ? status.fill : undefined,
                  borderColor: selectedStatus?.statusKey === status.statusKey ? status.fill : 'transparent'
                }}
              >
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: status.fill }} />
                {status.name} ({status.value})
              </button>
            ))}
          </div>
          {showHelpMessage && (
            <div className="text-center py-8 bg-blue-50 rounded-lg mb-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 text-blue-600">
                <Info className="w-5 h-5" />
                <p className="font-medium">
                  Click on any status tab above to view detailed information
                </p>
              </div>
            </div>
          )}

          {selectedStatus && details[selectedStatus.statusKey] && (
            <div className="flex-1 flex flex-col">
              <div className="border rounded-lg overflow-hidden flex-1 flex flex-col">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Equipment Name</th>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Ship Name</th>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Actual Start</th>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Start End</th>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Actual End</th>
                      <th className="w-1/6 px-4 py-3 text-left text-sm font-medium text-gray-700">Equipment Activity Flowchart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 flex-1">
                    {currentItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm truncate">{item.equipment_name}</td>
                        <td className="px-4 py-3 text-sm truncate">{item.project_name}</td>
                        <td className="px-4 py-3 text-sm truncate">{item.actual_start}</td>
                        <td className="px-4 py-3 text-sm truncate">{item.start_end}</td>
                        <td className="px-4 py-3 text-sm truncate">{item.actual_end}</td>
                        <td className="px-4 py-3 text-sm">
                          <button 
                            onClick={() => handleViewFlowchart(item.equipment_name, item.task_name)}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            View Flowchart
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
                    <div className="text-sm text-gray-700">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                      {Math.min(currentPage * itemsPerPage, details[selectedStatus.statusKey].length)} of{' '}
                      {details[selectedStatus.statusKey].length} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === 1 
                            ? 'bg-gray-200 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 bg-white border border-gray-300 rounded-md">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === totalPages 
                            ? 'bg-gray-200 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showActivityFlow && (
        <EquipmentZigZagSteps 
          isOpen={showActivityFlow}
          onClose={() => setShowActivityFlow(false)}
          equipmentName={selectedEquipment.name}
          taskName={selectedEquipment.taskName}
          projectId={ship?.id || project?.id}
          equipmentId={equipmentId}
        />
      )}
    </div>
  );
};

const EquipmentZigZagSteps = ({ isOpen, onClose, equipmentName, taskName, projectId, equipmentId }) => {
  const [stepsData, setStepsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !equipmentId || !projectId) return;

    const fetchStepsData = async () => {
      try {
        setLoading(true);
        const response = await getRequest(
          `transaction/equipment-data-chart/?project_id=${projectId}&equipment_id=${equipmentId}`
        );
        
        if (response.data) {
          // Sort the data by sequence in ascending order (1, 2, 3, etc.)
          const sortedData = [...response.data].sort((a, b) => a.sequence - b.sequence);
          setStepsData(sortedData);
        } else {
          setError('No data available for this equipment');
        }
      } catch (err) {
        setError('Failed to fetch equipment steps data');
        console.error('Error fetching equipment steps:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStepsData();
  }, [isOpen, projectId, equipmentId]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not started';
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}, ${date.getFullYear()}`;
  };

  const getStatus = (step) => {
    if (step.is_completed) return 'completed';
    if (step.actual_start) return 'in-progress';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in-progress': return 'bg-blue-500';
      case 'pending': return 'bg-red-300';
      default: return 'bg-red-400';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'in-progress': return 'text-blue-600';
      case 'pending': return 'text-red-500';
      default: return 'text-red-400';
    }
  };

  const getStepIcon = (status) => {
    const baseClasses = "h-5 w-5";
    const colorClass = getStatusTextColor(status);
    const combinedClasses = `${baseClasses} ${colorClass}`;
    
    if (status === 'completed') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={combinedClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (status === 'in-progress') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={combinedClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={combinedClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl w-full mx-auto max-h-[80vh] flex flex-col">
        <div className="bg-blue-500 text-white px-4 py-3 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Equipment Progress: {equipmentName}</h2>
            {taskName && <p className="text-sm opacity-90">Task: {taskName}</p>}
          </div>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-blue-600 rounded"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-10">
              <p>Loading equipment progress data...</p>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">
              <p>{error}</p>
            </div>
          ) : stepsData.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <p>No progress data available for this equipment</p>
            </div>
          ) : (
            <div className="py-2">
              <div className="relative">
                <div className="hidden md:block absolute h-full w-0.5 bg-gray-200 left-1/2 transform -translate-x-1/2"></div>
                
                {stepsData.map((step, index) => {
                  const isEven = index % 2 === 1;
                  const status = getStatus(step);
                  const statusColor = getStatusColor(status);
                  
                  return (
                    <div key={step.sequence} className="relative mb-6">
                      <div className={`flex flex-col md:flex-row ${isEven ? 'md:flex-row-reverse' : ''} items-center`}>
                        <div className={`flex w-full md:w-1/2 ${isEven ? 'md:justify-start md:pl-8' : 'md:justify-end md:pr-8'} mb-2 md:mb-0`}>
                          <div className={`rounded-lg bg-white shadow-md border p-4 ${
                            status === 'completed' ? 'border-green-200' : 
                            status === 'in-progress' ? 'border-blue-200' : 
                            'border-red-400'
                          } w-full md:max-w-xs`}>
                            <div className="flex items-center mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white mr-3 ${statusColor}`}>
                                {step.sequence}
                              </div>
                              <h3 className="font-semibold text-gray-900">{step.activity_name}</h3>
                              {getStepIcon(status)}
                            </div>
                            
                            <div className="text-sm">
                              <div className="flex justify-between mb-1">
                                <span className="text-gray-500">Status:</span>
                                <span className={`font-medium ${getStatusTextColor(status)}`}>
                                  {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                                </span>
                              </div>
                              
                              {status !== 'pending' && (
                                <>
                                  <div className="flex justify-between mb-1">
                                    <span className="text-gray-500">Started:</span>
                                    <span className="text-gray-700">{formatDate(step.actual_start)}</span>
                                  </div>
                                  {status === 'completed' && (
                                    <div className="flex justify-between mb-1">
                                      <span className="text-gray-500">Completed:</span>
                                      <span className="text-gray-700">{formatDate(step.actual_finish)}</span>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              <div className="flex justify-between">
                                <span className="text-gray-500">Completion:</span>
                                <span className="text-gray-700 font-medium">
                                  {step.actual_completion_percent}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="hidden md:block w-1/2"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DepartmentDonutChart = ({
  selectedDepartment: propSelectedDepartment,
  selectedProject: propSelectedProject,
  selectedShip: propSelectedShip,
  onDepartmentChange,
  onProjectChange,
  onShipChange,
  onClearFilters,
  departments,
  projects,
  ships
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [detailsData, setDetailsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isShipDropdownOpen, setIsShipDropdownOpen] = useState(false);
  const chartRef = useRef(null);

  const selectedDepartment = propSelectedDepartment;
  const selectedProject = propSelectedProject;
  const selectedShip = propSelectedShip;

  useEffect(() => {
    if (selectedDepartment) {
      fetchDepartmentData(
        selectedDepartment.id,
        selectedProject?.id,
        selectedShip?.id
      );
    }
  }, [selectedDepartment, selectedProject, selectedShip]);

  const fetchDepartmentData = async (departmentId, projectId = null, shipId = null) => {
    setLoading(true);
    try {
      let url = `transaction/equipment-charts/?equipment__department=${departmentId}`;
      
      if (shipId) {
        url += `&project_id=${shipId}`;
      } else if (projectId) {
        url += `&project_id=${projectId}`;
      }
      
      const response = await getRequest(url);
      const formattedData = formatChartData(response.summary);
      setChartData(formattedData);
      setDetailsData(response.details);
      if (response.project_name) {
        setProjectName(response.project_name);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch department data');
      console.error('Error fetching department data:', err);
      setChartData([]);
      setDetailsData({});
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (summary) => {
    return Object.entries(summary)
      .filter(([_, value]) => value > 0)
      .map(([statusKey, value]) => {
        const displayInfo = getStatusDisplayInfo(statusKey);
        return {
          name: displayInfo.name,
          value,
          statusKey,
          shortName: displayInfo.shortName,
          fill: generateStatusColor(statusKey)
        };
      });
  };

  const handleDepartmentChange = (department) => {
    onDepartmentChange(department);
    setIsDepartmentDropdownOpen(false);
  };

  const handleProjectChange = (project) => {
    onProjectChange(project);
    setIsProjectDropdownOpen(false);
  };

  const handleShipChange = (ship) => {
    onShipChange(ship);
    setIsShipDropdownOpen(false);
  };

  const handleClearFilters = () => {
    onClearFilters();
  };

  const buildChartTitle = () => {
    let title = `Department Status - ${selectedDepartment?.name || ''}`;
    if (selectedProject) title += ` - ${selectedProject.name}`;
    if (selectedShip) title += ` - ${selectedShip.name}`;
    if (projectName && !selectedProject && !selectedShip) title += ` - ${projectName}`;
    return title;
  };

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="text-center py-10">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (loading || !selectedDepartment) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="text-center py-10">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Equipment status overview by department</h2>
          <p className="text-gray-500 text-sm">{buildChartTitle()}</p>
        </div>
        
        <div className="flex flex-wrap items-end gap-3 mt-4 md:mt-0">
          <div className="relative">
            <span className="text-sm text-gray-500 font-bold mr-1">Department:</span>
            <button
              onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
              className="flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-600 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {selectedDepartment.name}
              <ChevronDown className="h-4 w-4 ml-2 transition-transform" />
            </button>
            
            {isDepartmentDropdownOpen && (
              <div className="absolute z-10 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-600 right-0 max-h-60 overflow-y-auto">
                {departments.map((department) => (
                  <button
                    key={department.id}
                    onClick={() => handleDepartmentChange(department)}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${
                      department.id === selectedDepartment.id ? 'bg-indigo-50 text-indigo-700' : ''
                    }`}
                  >
                    {department.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <span className="text-sm text-gray-500 font-bold mr-1">Project:</span>
            <button
              onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
              className="flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-600 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {selectedProject ? selectedProject.name : "Select Project"}
              <ChevronDown className="h-4 w-4 ml-2 transition-transform" />
            </button>
            
            {isProjectDropdownOpen && (
              <div className="absolute z-10 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-600 right-0 max-h-60 overflow-y-auto">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectChange(project)}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${
                      selectedProject && project.id === selectedProject.id ? 'bg-indigo-50 text-indigo-700' : ''
                    }`}
                  >
                    {project.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedProject && (
            <div className="relative">
              <span className="text-sm text-gray-500 font-bold mr-1">Ship:</span>
              <button
                onClick={() => setIsShipDropdownOpen(!isShipDropdownOpen)}
                className="flex items-center justify-between px-4 py-2 bg-gray-50 border border-gray-600 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {selectedShip ? selectedShip.name : "Select Ship"}
                <ChevronDown className="h-4 w-4 ml-2 transition-transform" />
              </button>
              
              {isShipDropdownOpen && (
                <div className="absolute z-10 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-600 right-0 max-h-60 overflow-y-auto">
                  {ships.length > 0 ? (
                    ships.map((ship) => (
                      <button
                        key={ship.id}
                        onClick={() => handleShipChange(ship)}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${
                          selectedShip && ship.id === selectedShip.id ? 'bg-indigo-50 text-indigo-700' : ''
                        }`}
                      >
                        {ship.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No ships available</div>
                  )}
                </div>
              )}
            </div>
          )}

          {(selectedProject || selectedShip) && (
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 border-gray-600 border-1"
            >
              Clear Filters
            </button>
          )}

          <button 
            onClick={() => setShowDetails(!showDetails)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            title="Toggle Details"
          >
            <Info className="h-4 w-4 mr-1" />
            {showDetails ? "Hide Details" : "Show Details"}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-6 bg-gray-50 p-6 rounded-xl">
        {chartData.length > 0 ? (
          <>
            <div ref={chartRef} className="h-96 w-96 relative">
              <ResponsiveContainer width="110%" height="110%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius="20%"
                    outerRadius="100%"
                    dataKey="value"
                    animationDuration={800}
                    startAngle={90}
                    endAngle={-270}
                    label={renderCustomizedLabel}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.fill}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-1 ml-10 gap-4">
                {chartData.map((entry, index) => (
                  <div 
                    key={entry.statusKey} 
                    className="flex items-center p-1 rounded-lg"
                  >
                    <div 
                      className="w-5 h-5 rounded-full mr-2" 
                      style={{ backgroundColor: entry.fill }}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-800">{entry.name}</span>
                        <span className="font-bold text-lg" style={{ color: entry.fill }}>
                          {entry.value}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="h-2.5 rounded-full transition-all" 
                          style={{ 
                            width: `${(entry.value / Math.max(...chartData.map(d => d.value))) * 100}%`,

                            backgroundColor: entry.fill
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="w-full text-center py-10">
            <p>No data available for the selected filters</p>
          </div>
        )}
      </div>

      {showDetails && chartData.length > 0 && (
        <StatusDetailsModal 
          data={chartData}
          details={detailsData}
          department={selectedDepartment.name}
          project={selectedProject}
          ship={selectedShip}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
};

DepartmentDonutChart.propTypes = {
  selectedDepartment: PropTypes.object,
  selectedProject: PropTypes.object,
  selectedShip: PropTypes.object,
  onDepartmentChange: PropTypes.func,
  onProjectChange: PropTypes.func,
  onShipChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  departments: PropTypes.array,
  projects: PropTypes.array,
  ships: PropTypes.array,
};

export default DepartmentDonutChart;